/**
 * Tool wrappers for extension-registered tools.
 *
 * These wrappers only adapt tool execution so extension tools receive the runner context.
 * Tool call and tool result interception is handled by AgentSession via agent-core hooks.
 */

import type { AgentTool } from "@earendil-works/pi-agent-core";
import { withBuiltinDiagnostics } from "../builtin-diagnostics.ts";
import { wrapToolDefinition } from "../tools/tool-definition-wrapper.ts";
import type { ExtensionRunner } from "./runner.ts";
import type { RegisteredTool } from "./types.ts";

/**
 * Wrap a RegisteredTool into an AgentTool.
 * Uses the runner's createContext() for consistent context across tools and event handlers.
 */
export function wrapRegisteredTool(registeredTool: RegisteredTool, runner: ExtensionRunner): AgentTool {
	return {
		...wrapToolDefinition(registeredTool.definition),
		execute: (id, args, signal, update) =>
			withBuiltinDiagnostics(runner, async () => {
				const lifetime = new AbortController();
				const callSignal = signal ? AbortSignal.any([signal, lifetime.signal]) : lifetime.signal;
				const tool = wrapToolDefinition(registeredTool.definition, () => runner.createToolContext(id, callSignal));
				try {
					return await tool.execute(id, args, callSignal, update);
				} finally {
					lifetime.abort();
				}
			}),
	};
}

/**
 * Wrap all registered tools into AgentTools.
 * Uses the runner's createContext() for consistent context across tools and event handlers.
 */
export function wrapRegisteredTools(registeredTools: RegisteredTool[], runner: ExtensionRunner): AgentTool[] {
	return registeredTools.map((tool) => wrapRegisteredTool(tool, runner));
}
